import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {UiHomeTileSizeEnum} from "../../../model/dto/enum/ui-home-tile-size.enum";
import {UiQlWidgetViolationDto} from "../../../model/dto/api/ui-ql-widget-violation.dto";
import {UiQlWidgetSaveDialogValue} from "../../../interface/ui-ql-widget-save-dialog-value.interface";
import {UiQlWidgetSizeOption} from "../../../interface/ui-ql-widget-size-option.interface";
import {uiQlViolationText} from "../../../logic/ui-ql-violation-texts";

/**
 * Save dialog of the QL workbench (builder mode only): name, description, offered tile sizes and
 * the default size. The dialog does NOT call the backend — it emits {@link onSave} and the page
 * performs `createDefinition` / `updateDefinition` and hands back `busy`, `violations` (HTTP 400)
 * and `errorMessage` (409, 403, technical errors) for display.
 *
 * Declared in `UiQlWidgetBuilderModule` (NgModule, fe_core_standards) - its only host is
 * `UiQlWidgetWorkbenchPage` of the same module.
 */
@Component({
    selector: 'ui-ql-widget-save-dialog',
    templateUrl: './ui-ql-widget-save-dialog.component.html',
    styleUrls: ['./ui-ql-widget-save-dialog.component.scss'],
    standalone: false,
})
export class UiQlWidgetSaveDialogComponent implements OnChanges {

    /** The four sizes of `UiHomeTileSizeEnum` (api-contract.md §Enums), in enum order. */
    static readonly SIZE_OPTIONS: UiQlWidgetSizeOption[] = [
        {label: '1 × 1', value: UiHomeTileSizeEnum.s1x1},
        {label: '2 × 2', value: UiHomeTileSizeEnum.s2x2},
        {label: '4 × 2', value: UiHomeTileSizeEnum.s4x2},
        {label: '4 × 4', value: UiHomeTileSizeEnum.s4x4},
    ];

    @Input() visible: boolean = false;

    /** Prefill; read whenever the dialog is (re)opened. */
    @Input() initialValue: UiQlWidgetSaveDialogValue | null = null;

    /** Alias of an already saved widget — shown read-only (immutable after the first save). */
    @Input() alias: string | null = null;

    @Input() busy: boolean = false;

    /** Policy violations of a rejected save (HTTP 400). */
    @Input() violations: UiQlWidgetViolationDto[] = [];

    /** A German message for 409 (alias exists), 403 or a technical failure. */
    @Input() errorMessage: string | null = null;

    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() onSave = new EventEmitter<UiQlWidgetSaveDialogValue>();

    readonly sizeOptions = UiQlWidgetSaveDialogComponent.SIZE_OPTIONS;

    name: string = '';
    description: string = '';
    sizes: UiHomeTileSizeEnum[] = [];
    defaultSize: UiHomeTileSizeEnum | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible) {
            this.applyInitialValue();
        }
    }

    /** Only the sizes that are offered may be the default. */
    get defaultSizeOptions(): UiQlWidgetSizeOption[] {
        return this.sizeOptions.filter(o => this.sizes.includes(o.value));
    }

    get canSubmit(): boolean {
        return !this.busy
            && this.name.trim().length > 0
            && this.sizes.length > 0
            && this.defaultSize !== null
            && this.sizes.includes(this.defaultSize);
    }

    onSizesChange(sizes: UiHomeTileSizeEnum[]): void {
        this.sizes = [...(sizes ?? [])].sort((a, b) => a - b);
        if (this.defaultSize === null || !this.sizes.includes(this.defaultSize)) {
            this.defaultSize = this.sizes.length ? this.sizes[0] : null;
        }
    }

    submit(): void {
        if (!this.canSubmit) {
            return;
        }
        this.onSave.emit({
            name: this.name.trim(),
            description: this.description.trim(),
            sizes: [...this.sizes],
            defaultSize: this.defaultSize!,
        });
    }

    /** German text of a violation (fixed text per code, never the raw server message). */
    violationText(violation: UiQlWidgetViolationDto): string {
        return uiQlViolationText(violation);
    }

    close(): void {
        if (this.busy) {
            return;
        }
        this.visible = false;
        this.visibleChange.emit(false);
    }

    private applyInitialValue(): void {
        const value = this.initialValue;
        this.name = value?.name ?? '';
        this.description = value?.description ?? '';
        this.sizes = value?.sizes?.length ? [...value.sizes].sort((a, b) => a - b) : [];
        this.defaultSize = value?.defaultSize ?? null;
        this.onSizesChange(this.sizes);
    }
}
